import { isValidURL } from "./index";

export const tenantValidationForActivation = (tenant) => {
  if (!tenant) return false;

  // Helper functions
  const validateRequiredString = (value, minLength, fieldName) => {
    if (!value || value.length < minLength) {
      console.error(`Invalid ${fieldName}: Must have at least ${minLength} characters`);
      return false;
    }
    return true;
  };

  const validateOptionalString = (value, minLength, fieldName) => {
    if (value && value.length < minLength) {
      console.error(`Invalid ${fieldName}: Must have at least ${minLength} characters if present`);
      return false;
    }
    return true;
  };

  const validateNotNone = (value, fieldName) => {
    if (!value || value === "none") {
      console.error(`Invalid ${fieldName}: Must be defined and not 'none'`);
      return false;
    }
    return true;
  };

  const validateStringType = (value, fieldName) => {
    if (!value || typeof value !== "string") {
      console.error(`Invalid ${fieldName}: Must be a valid string`);
      return false;
    }
    return true;
  };

  const validateURLField = (value, fieldName, minLength = 12) => {
    if (!value || !isValidURL(value) || value.trim().length < minLength) {
      console.error(`Invalid ${fieldName}: Must be a valid URL with at least ${minLength} characters.`);
      return false;
    }
    return true;
  };

  const validateKeyField = (value, fieldName, minLength) => {
    if (!value || value.trim().length < minLength) {
      console.error(`Invalid ${fieldName}: Must contain at least ${minLength} characters.`);
      return false;
    }
    return true;
  };

  // Validation steps
  const validations = [
    () => validateRequiredString(tenant.tenant_label, 3, "tenant_label"),
    () => validateRequiredString(tenant.tenant_name, 3, "tenant_name"),
    () => validateOptionalString(tenant.description, 3, "description"),
    () => validateOptionalString(tenant.tax_id, 9, "tax_id"),
    () => validateNotNone(tenant.base_locale, "base_locale"),
    () => validateNotNone(tenant.base_currency, "base_currency"),
    () => validateStringType(tenant.parameters, "parameters"),
    () => validateStringType(tenant.templates, "templates"),
    // Validate ct_config
    () => {
      const { ct_config } = tenant;
      if (!ct_config) {
        console.error("Invalid tenant: Missing `ct_config`");
        return false;
      }
      const requiredFields = [
        { key: "project_key", minLength: 2 },
        { key: "client_id", minLength: 10 },
        { key: "auth_url", minLength: 10, isUrl: true },
        { key: "scopes", minLength: 10 },
        { key: "host", minLength: 10, isUrl: true },
        { key: "order_type", minLength: 2 },
        { key: "lineitem_type", minLength: 2 },
        { key: "customer_type", minLength: 2 },
        { key: "payment_type", minLength: 2 },
        { key: "transaction_type", minLength: 2 },
      ];
      for (const field of requiredFields) {
        const value = ct_config[field.key];
        if (!value || value.length < field.minLength) {
          console.error(`Invalid ${field.key}: Must have at least ${field.minLength} characters`);
          return false;
        }
        if (field.isUrl && !isValidURL(value)) {
          console.error(`Invalid ${field.key}: Must be a valid URL`);
          return false;
        }
      }
      return true;
    },
    // Validate tenant URLs and keys
    () => {
      const {
        webhook_url,
        webhook_verification_key,
        cart_retrieval_url,
        return_cart_retrieval_url,
        token_retrieval_url,
        callback_api_key,
        key_sync_url,
        sync_api_key,
      } = tenant;
      const fieldValidations = [
        () => validateURLField(webhook_url, "Webhook URL"),
        () => validateURLField(cart_retrieval_url, "Cart Retrieval URL"),
        () => validateURLField(return_cart_retrieval_url, "Return Cart Retrieval URL"),
        () => validateURLField(token_retrieval_url, "Token Retrieval URL"),
        () => validateKeyField(callback_api_key, "Callback API Key", 12),
        () => validateURLField(key_sync_url, "Key Sync URL"),
        () => validateKeyField(sync_api_key, "Sync API Key", 12),
      ];
      return fieldValidations.every((fn) => fn());
    },
    // Validate theme
    () => {
      const {
        fontFamily,
        textColor,
        backgroundColor,
        primaryColor,
        primaryContrastTextColor,
        secondaryColor,
        secondaryContrastTextColor,
        tertiaryColor,
        tertiaryContrastTextColor,
      } = tenant.theme || {};
      const themeFields = {
        fontFamily,
        textColor,
        backgroundColor,
        primaryColor,
        primaryContrastTextColor,
        secondaryColor,
        secondaryContrastTextColor,
        tertiaryColor,
        tertiaryContrastTextColor,
      };
      const missingFields = Object.entries(themeFields)
        .filter(([, value]) => !value || !value.trim())
        .map(([key]) => `theme.${key}`);
      if (missingFields.length) {
        console.error(
          "Theme validation failed for activation:",
          missingFields.join(", ")
        );
        return false;
      }
      return true;
    },
  ];

  // Execute all validations
  for (const validate of validations) {
    if (!validate()) {
      console.error("Tenant validation failed for activation.");
      return false;
    }
  }

  // All validations passed
  return true;
};
